/**
 * TimelineScreen — «Смена: столы × заказы». Факт-чек сшивки зона↔стол: по каждой
 * зоне-столу лента занятости по камере (zone_occupancy), посадки (guest_seated)
 * и заказы POS по привязанному коду стола; расхождения — в минутах. Для сверки
 * по архиву NVR у лейна указан канал камеры. Поллинг (канала для таймлайна нет).
 */
import { useCallback, useEffect, useState } from "react";
import type { ShiftTimeline, TimelineLane, TimelineOrder } from "@/types/contract";
import { api, UnauthorizedError } from "@/api/client";
import { ScreenHeader } from "@/components/ScreenHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { AlertIcon } from "@/components/ui/icons";

interface Props {
  restaurantId: string;
}

type Phase =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

const POLL_MS = 60_000;

const MAPPING_META: Record<string, { label: string; cls: string }> = {
  confirmed: { label: "подтверждён", cls: "border-accent/40 bg-accent/10 text-accent" },
  candidate: { label: "кандидат", cls: "border-warning/40 bg-warning/10 text-warning" },
  ambiguous: { label: "неоднозначно", cls: "border-border bg-surface-2 text-muted" },
};

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Положение метки времени на ленте, % от окна. */
function pct(iso: string, fromMs: number, toMs: number): number {
  const t = new Date(iso).getTime();
  if (toMs <= fromMs) return 0;
  return Math.min(100, Math.max(0, ((t - fromMs) / (toMs - fromMs)) * 100));
}

/** Часовые деления окна для оси. */
function hourTicks(fromMs: number, toMs: number): number[] {
  const ticks: number[] = [];
  const first = new Date(fromMs);
  first.setMinutes(0, 0, 0);
  for (let t = first.getTime(); t <= toMs; t += 3_600_000) {
    if (t >= fromMs) ticks.push(t);
  }
  return ticks;
}

export function TimelineScreen({ restaurantId }: Props) {
  const [data, setData] = useState<ShiftTimeline | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setPhase({ kind: "loading" });
      try {
        const t = await api.getTableTimeline({ restaurant_id: restaurantId });
        setData(t);
        setPhase({ kind: "ready" });
      } catch (e) {
        const message =
          e instanceof UnauthorizedError
            ? "Сессия истекла — войдите в Orees Cloud снова."
            : e instanceof Error
              ? e.message
              : "Неизвестная ошибка";
        setPhase({ kind: "error", message });
      }
    },
    [restaurantId],
  );

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const lanes = data ? sortLanes(data.lanes) : [];
  const mismatch = lanes.filter((l) => l.occupied_no_order_min >= 10).length;

  return (
    <>
      <ScreenHeader
        eyebrow="Факт-чек"
        title="Смена: столы × заказы"
        subtitle={
          data && phase.kind === "ready"
            ? `${fmtTime(data.from)}–${fmtTime(data.to)}` +
              (data.shift ? ` · смена открыта` : " · смены нет (12 ч)") +
              (mismatch > 0 ? ` · расхождения: ${mismatch}` : "")
            : "Занятость столов по камерам против заказов POS"
        }
      />

      {phase.kind === "error" && (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <AlertIcon size={26} className="text-danger" />
          <h3 className="text-base font-semibold text-ink">
            Не удалось загрузить таймлайн
          </h3>
          <p className="max-w-sm text-sm text-muted">{phase.message}</p>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Повторить
          </Button>
        </Card>
      )}

      {phase.kind !== "error" && data && (
        <>
          {/* Легенда */}
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm bg-accent/35" /> занят (камера)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm border border-ink/50 bg-ink/10" /> заказ POS
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-ink" /> посадка
            </span>
          </div>

          {lanes.map((lane) => (
            <LaneCard key={lane.zone_id} lane={lane} from={data.from} to={data.to} />
          ))}

          {data.unmapped_orders.length > 0 && (
            <section className="mt-6">
              <h2 className="mb-2 text-sm font-semibold text-muted">
                Заказы на столах без зоны ({data.unmapped_orders.length})
              </h2>
              <Card className="divide-y divide-border p-0">
                {sortOrders(data.unmapped_orders).map((o) => (
                  <div
                    key={`${o.number}-${o.opened_at}`}
                    className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
                  >
                    <span className="tabular font-semibold text-ink">
                      Стол {o.table_code ?? "—"}
                    </span>
                    <span className="text-muted">
                      {fmtTime(o.opened_at)}
                      {o.closed_at ? `–${fmtTime(o.closed_at)}` : " · открыт"}
                    </span>
                    <span className="tabular text-muted">
                      {o.total != null ? `${Math.round(o.total).toLocaleString("ru-RU")} ₸` : "—"}
                    </span>
                  </div>
                ))}
              </Card>
            </section>
          )}
        </>
      )}
    </>
  );
}

function LaneCard({
  lane,
  from,
  to,
}: {
  lane: TimelineLane;
  from: string;
  to: string;
}) {
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  const meta = lane.mapping_status ? MAPPING_META[lane.mapping_status] : null;
  const warn = lane.occupied_no_order_min >= 10;

  return (
    <Card className="mb-2 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-ink">
          {lane.pos_table_code ? `Стол ${lane.pos_table_code}` : "Без привязки"}
        </span>
        <span className="text-[11px] text-muted">
          {lane.zone_id}
          {lane.nvr_channel != null && ` · NVR канал ${lane.nvr_channel}`}
        </span>
        {meta && (
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}
          >
            {meta.label}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2 text-[11px]">
          {lane.occupied_no_order_min > 0 && (
            <span className={warn ? "font-semibold text-warning" : "text-muted"}>
              занят без заказа {lane.occupied_no_order_min} мин
            </span>
          )}
          {lane.order_no_occupancy_min > 0 && (
            <span className="text-muted">
              заказ без гостей {lane.order_no_occupancy_min} мин
            </span>
          )}
        </span>
      </div>

      {/* Лента: занятость (верх), заказы (низ), посадки (точки) */}
      <div className="relative h-9 overflow-hidden rounded-control bg-surface-2">
        {hourTicks(fromMs, toMs).map((t) => (
          <span
            key={t}
            className="absolute inset-y-0 w-px bg-border"
            style={{ left: `${((t - fromMs) / (toMs - fromMs)) * 100}%` }}
          />
        ))}
        {lane.occupancy.map((iv, i) => (
          <span
            key={`o${i}`}
            className="absolute top-1 h-3 rounded-sm bg-accent/35"
            style={{
              left: `${pct(iv.from, fromMs, toMs)}%`,
              width: `${Math.max(
                pct(iv.to, fromMs, toMs) - pct(iv.from, fromMs, toMs),
                0.6,
              )}%`,
            }}
            title={`Занят ${fmtTime(iv.from)}–${fmtTime(iv.to)}`}
          />
        ))}
        {lane.orders.map((o, i) => (
          <span
            key={`p${i}`}
            className="absolute bottom-1 h-3 rounded-sm border border-ink/50 bg-ink/10"
            style={{
              left: `${pct(o.opened_at, fromMs, toMs)}%`,
              width: `${Math.max(
                (o.closed_at ? pct(o.closed_at, fromMs, toMs) : 100) -
                  pct(o.opened_at, fromMs, toMs),
                0.6,
              )}%`,
            }}
            title={`Заказ №${o.number} ${fmtTime(o.opened_at)}${
              o.closed_at ? `–${fmtTime(o.closed_at)}` : " (открыт)"
            }${o.total != null ? ` · ${Math.round(o.total).toLocaleString("ru-RU")} ₸` : ""}`}
          />
        ))}
        {lane.seatings.map((s, i) => (
          <span
            key={`s${i}`}
            className="absolute top-[3px] h-1.5 w-1.5 rounded-full bg-ink"
            style={{ left: `calc(${pct(s, fromMs, toMs)}% - 3px)` }}
            title={`Посадка ${fmtTime(s)}`}
          />
        ))}
      </div>

      {lane.orders.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted">
          {lane.orders.map((o) => (
            <span key={`${o.number}-${o.opened_at}`} className="tabular">
              №{o.number} · {fmtTime(o.opened_at)}
              {o.closed_at ? `–${fmtTime(o.closed_at)}` : " · открыт"}
              {o.total != null && ` · ${Math.round(o.total).toLocaleString("ru-RU")} ₸`}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Столы с кодом — по номеру, затем зоны без привязки по id. */
function sortLanes(lanes: TimelineLane[]): TimelineLane[] {
  return [...lanes].sort((a, b) => {
    if (a.pos_table_code && b.pos_table_code)
      return Number(a.pos_table_code) - Number(b.pos_table_code);
    if (a.pos_table_code) return -1;
    if (b.pos_table_code) return 1;
    return a.zone_id.localeCompare(b.zone_id, "ru");
  });
}

function sortOrders(orders: TimelineOrder[]): TimelineOrder[] {
  return [...orders].sort((a, b) => a.opened_at.localeCompare(b.opened_at));
}
